// Dialogo reusable para crear/editar un vehiculo de TRS Rastreo — extraido
// de TrsRastreoVehiculosPage.jsx (mismos campos, mismo selector de iconos)
// para poder abrirlo tambien desde el arbol de ClientePlataformaPanel.jsx.
import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import {
  crearVehiculo,
  editarGrupo,
  editarVehiculo,
  listarGrupos,
  listarIconos,
  listarUsuariosSimple,
  listarVehiculos,
  vincularUsuarioVehiculo,
} from "../services/trsRastreoService";

const emptyForm = {
  veh_alias: "", veh_placa: "", veh_modid: "", veh_observaciones: "S/N", veh_icono: "", veh_activo: true,
  // asignacionTipo: "grupo" | "usuario" | "" — un vehiculo nuevo se asigna a
  // UNO de los dos (no ambos a la vez), y es obligatorio elegir cual.
  asignacionTipo: "",
  grupoIds: [], usuarioIds: [],
};

// Checklist generico (grupos o usuarios) para asignar al crear un vehiculo —
// ninguno de los dos listados de TRS pagina ni busca server-side, asi que se
// filtra en el cliente sobre la lista completa ya cargada.
function SimpleCheckList({ title, items, loading, selectedIds, onChange, filter, onFilterChange, getLabel }) {
  const selectedSet = new Set(selectedIds.map(String));
  const f = filter.trim().toLowerCase();
  const filtrados = f ? items.filter((it) => getLabel(it).toLowerCase().includes(f)) : items;

  const toggle = (id) => {
    const value = String(id);
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange([...next]);
  };

  return (
    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 0.75, display: "flex", alignItems: "center", gap: 0.75, borderBottom: "1px solid #e5e7eb" }}>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 900, flex: 1 }}>{title}</Typography>
        <Chip size="small" label={`${selectedSet.size} seleccionado(s)`} sx={{ height: 20, fontSize: "0.68rem" }} />
      </Box>
      <Box sx={{ p: 0.75, borderBottom: "1px solid #e5e7eb" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar..."
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          InputProps={{ endAdornment: loading ? <CircularProgress color="inherit" size={16} /> : null }}
          sx={{ "& .MuiInputBase-input": { py: 0.55, fontSize: "0.76rem" } }}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2.5 }}><CircularProgress size={18} /></Box>
        ) : filtrados.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 1, fontStyle: "italic" }}>
            Sin registros para mostrar.
          </Typography>
        ) : (
          filtrados.map((it) => {
            const id = it.id;
            const checked = selectedSet.has(String(id));
            return (
              <Box
                key={id}
                onClick={() => toggle(id)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.5, px: 0.75, py: 0.35, cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Checkbox size="small" checked={checked} sx={{ p: 0.25 }} />
                <Typography sx={{ fontSize: "0.76rem", fontWeight: checked ? 900 : 600 }}>
                  {getLabel(it)}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}

export default function TrsVehiculoFormDialog({
  open,
  mode = "create", // "create" | "edit"
  vehiculo = null, // requerido cuando mode="edit"
  codServicioCliente = null, // si se pasa (vista de un cliente puntual), la pestaña Usuario solo lista los usuarios de ese cliente
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadingVehiculo, setLoadingVehiculo] = useState(false);
  const [vehiculoCompleto, setVehiculoCompleto] = useState(null);
  // El formulario tiene scroll (icono, grupos, etc.) — un Alert arriba del
  // todo puede quedar fuera de vista si el usuario ya bajo hasta Guardar, asi
  // que el error tambien se muestra en un Snackbar fijo abajo.
  const [snack, setSnack] = useState({ open: false, msg: "" });

  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [iconos, setIconos] = useState([]);
  const [iconosCargados, setIconosCargados] = useState(false);
  const [iconosLoading, setIconosLoading] = useState(false);

  // Grupos y usuarios directos para asignar al crear el vehiculo — no aplica
  // en edicion (la membresia de grupo/usuario de un vehiculo ya existente se
  // maneja desde "Editar grupo" o el vinculo directo, no desde este dialogo).
  const [grupos, setGrupos] = useState([]);
  const [gruposLoading, setGruposLoading] = useState(false);
  const [grupoFilter, setGrupoFilter] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosLoading, setUsuariosLoading] = useState(false);
  const [usuarioFilter, setUsuarioFilter] = useState("");

  // El vehiculo que llega desde el arbol de grupos de un cliente
  // (cuadrilla_relacion[].info_relacionado) es un resumen liviano — no trae
  // veh_icono ni necesariamente todos los campos que exige editarVehiculo.
  // Por eso, al abrir en modo edicion, se busca primero el registro completo
  // por placa (mismo endpoint que ya usa TrsRastreoVehiculosPage.jsx) antes
  // de armar el formulario.
  useEffect(() => {
    if (!open) return;
    setError("");

    const initForm = (v) => {
      setVehiculoCompleto(v);
      setForm({
        veh_alias: v.veh_alias || "",
        veh_placa: v.veh_placa || "",
        veh_modid: v.veh_modid || "",
        veh_observaciones: v.veh_observaciones || "",
        veh_icono: v.veh_icono || "",
        veh_activo: v.veh_activo !== false,
        asignacionTipo: "",
        grupoIds: [],
        usuarioIds: [],
      });
    };

    if (!isEdit || !vehiculo) {
      setVehiculoCompleto(null);
      setForm(emptyForm);
      setGrupoFilter("");
      setGrupos([]);
      setGruposLoading(true);
      listarGrupos()
        .then((resp) => setGrupos(resp?.data?.data || []))
        .catch(() => setGrupos([]))
        .finally(() => setGruposLoading(false));
      setUsuarioFilter("");
      setUsuarios([]);
      setUsuariosLoading(true);
      listarUsuariosSimple()
        .then((resp) => {
          const todos = resp?.data?.data || [];
          // Si se abrio desde la vista de un cliente puntual, solo mostrar sus
          // propios usuarios de TRS Rastreo (usu_cliente = cod_servicio_cliente),
          // no el listado completo del sistema.
          const filtrados = codServicioCliente
            ? todos.filter((u) => Number(u.usu_cliente) === Number(codServicioCliente))
            : todos;
          setUsuarios(filtrados);
        })
        .catch(() => setUsuarios([]))
        .finally(() => setUsuariosLoading(false));
      return;
    }

    initForm(vehiculo);

    const placa = String(vehiculo.veh_placa || "").trim();
    if (!placa) return;

    setLoadingVehiculo(true);
    listarVehiculos({ buscar: placa, pageSize: 5 })
      .then((resp) => {
        const data = resp?.data?.data || [];
        const match =
          data.find((v) => String(v.id) === String(vehiculo.id)) ||
          data.find((v) => String(v.veh_placa).trim().toUpperCase() === placa.toUpperCase()) ||
          null;
        if (match) initForm(match);
      })
      .catch(() => {})
      .finally(() => setLoadingVehiculo(false));
  }, [open, isEdit, vehiculo, codServicioCliente]);

  const abrirSelectorIcono = async () => {
    setIconPickerOpen(true);
    if (iconosCargados) return;
    setIconosLoading(true);
    try {
      const res = await listarIconos();
      const data = res?.data?.data ?? res?.data;
      setIconos(Array.isArray(data) ? data : []);
      setIconosCargados(true);
    } catch (e) {
      setIconos([]);
    } finally {
      setIconosLoading(false);
    }
  };

  const seleccionarIcono = (url) => {
    setForm((p) => ({ ...p, veh_icono: url }));
    setIconPickerOpen(false);
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSave = async () => {
    const placa = form.veh_placa.trim();
    const modid = form.veh_modid.trim();
    if (!placa || !modid) {
      const msg = "Placa y Modem/IMEI son obligatorios.";
      setError(msg);
      setSnack({ open: true, msg });
      return;
    }
    if (!isEdit) {
      const tieneGrupos = form.asignacionTipo === "grupo" && form.grupoIds.length > 0;
      const tieneUsuarios = form.asignacionTipo === "usuario" && form.usuarioIds.length > 0;
      if (!tieneGrupos && !tieneUsuarios) {
        const msg = "Debe elegir Grupo o Usuario, y seleccionar al menos uno.";
        setError(msg);
        setSnack({ open: true, msg });
        return;
      }
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        veh_alias: form.veh_alias.trim() || placa,
        veh_placa: placa,
        veh_modid: modid,
        veh_observaciones: form.veh_observaciones.trim(),
        veh_icono: form.veh_icono.trim(),
        veh_activo: form.veh_activo,
      };
      if (isEdit) {
        await editarVehiculo(vehiculoCompleto?.id ?? vehiculo.id, payload);
      } else {
        const resp = await crearVehiculo(payload);
        const nuevoId = resp?.data?.data?.id;
        if (nuevoId && form.grupoIds.length) {
          try {
            await Promise.all(
              form.grupoIds.map((grupoId) => editarGrupo(grupoId, { vehiculos_agregar: [Number(nuevoId)] }))
            );
          } catch (e) {
            // El vehiculo SI se creo — solo avisa que la asignacion a grupo(s) fallo,
            // para que se pueda hacer despues desde "Editar grupo".
            const msg = e?.response?.data?.error || e?.response?.data?.message || e.message
              || "El vehiculo se registro, pero no se pudo asignar a los grupos seleccionados.";
            setSnack({ open: true, msg });
          }
        }
        if (nuevoId && form.usuarioIds.length) {
          try {
            await Promise.all(
              form.usuarioIds.map((usuarioId) => vincularUsuarioVehiculo({
                usu_id: Number(usuarioId), veh_id: Number(nuevoId), usuveh_activo: true,
              }))
            );
          } catch (e) {
            const msg = e?.response?.data?.error || e?.response?.data?.message || e.message
              || "El vehiculo se registro, pero no se pudo vincular a los usuarios seleccionados.";
            setSnack({ open: true, msg });
          }
        }
      }
      onSaved?.();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo guardar el vehiculo";
      setError(msg);
      setSnack({ open: true, msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth={isEdit ? "xs" : "sm"} fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 900 }}>
          {isEdit ? "Editar vehiculo" : "Registrar vehiculo"}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5 }}>
          {!!error && <Alert severity="error" sx={{ mb: 1.25 }}>{error}</Alert>}
          {loadingVehiculo ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: isEdit ? "1fr" : "1fr 1fr" }, gap: 1.25 }}>
              <Box sx={{ display: "grid", gap: 1.25, alignContent: "start" }}>
                <TextField
                  size="small"
                  label="Placa"
                  value={form.veh_placa}
                  onChange={(e) => setForm((p) => ({ ...p, veh_placa: e.target.value }))}
                  disabled={saving}
                  autoFocus
                  required
                />
                <TextField
                  size="small"
                  label="Alias"
                  value={form.veh_alias}
                  onChange={(e) => setForm((p) => ({ ...p, veh_alias: e.target.value }))}
                  disabled={saving}
                  helperText="Si se deja vacio, se usa la placa"
                />
                <TextField
                  size="small"
                  label="Modem / IMEI"
                  value={form.veh_modid}
                  onChange={(e) => setForm((p) => ({ ...p, veh_modid: e.target.value }))}
                  disabled={saving}
                  required
                />
                <TextField
                  size="small"
                  label="Observaciones"
                  value={form.veh_observaciones}
                  onChange={(e) => setForm((p) => ({ ...p, veh_observaciones: e.target.value }))}
                  disabled={saving}
                  multiline
                  minRows={2}
                />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Icono</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      src={form.veh_icono || undefined}
                      variant="rounded"
                      sx={{ width: 40, height: 40, bgcolor: "grey.200" }}
                    >
                      <DirectionsCarIcon sx={{ color: "grey.500" }} />
                    </Avatar>
                    <Button size="small" variant="outlined" onClick={abrirSelectorIcono} disabled={saving}>
                      Elegir icono
                    </Button>
                    {form.veh_icono && (
                      <Button size="small" color="inherit" onClick={() => setForm((p) => ({ ...p, veh_icono: "" }))} disabled={saving}>
                        Quitar
                      </Button>
                    )}
                  </Box>
                </Box>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={form.veh_activo}
                      onChange={(e) => setForm((p) => ({ ...p, veh_activo: e.target.checked }))}
                      disabled={saving}
                    />
                  )}
                  label="Activo"
                />
              </Box>

              {!isEdit && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={form.asignacionTipo}
                    onChange={(e, value) => {
                      if (!value) return;
                      // Se cambia el tipo de asignacion: se limpia la lista del
                      // otro tipo para que no quede una seleccion oculta.
                      setForm((p) => ({
                        ...p,
                        asignacionTipo: value,
                        grupoIds: value === "grupo" ? p.grupoIds : [],
                        usuarioIds: value === "usuario" ? p.usuarioIds : [],
                      }));
                    }}
                    disabled={saving}
                    fullWidth
                  >
                    <ToggleButton value="usuario" sx={{ textTransform: "none", fontWeight: 700 }}>
                      Usuario
                    </ToggleButton>
                    <ToggleButton value="grupo" sx={{ textTransform: "none", fontWeight: 700 }}>
                      Grupo
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {!form.asignacionTipo ? (
                    <Box sx={{ border: "1px dashed #d1d5db", borderRadius: 1, flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                        Elige si el vehículo se asigna a un grupo o directamente a un usuario.
                      </Typography>
                    </Box>
                  ) : form.asignacionTipo === "grupo" ? (
                    <SimpleCheckList
                      title="Grupos"
                      items={grupos}
                      loading={gruposLoading}
                      selectedIds={form.grupoIds}
                      onChange={(ids) => setForm((p) => ({ ...p, grupoIds: ids }))}
                      filter={grupoFilter}
                      onFilterChange={setGrupoFilter}
                      getLabel={(g) => g.cua_descripcion || "-"}
                    />
                  ) : (
                    <SimpleCheckList
                      title="Usuarios"
                      items={usuarios}
                      loading={usuariosLoading}
                      selectedIds={form.usuarioIds}
                      onChange={(ids) => setForm((p) => ({ ...p, usuarioIds: ids }))}
                      filter={usuarioFilter}
                      onFilterChange={setUsuarioFilter}
                      getLabel={(u) => u.usu_nombre || u.usu_username || "-"}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button size="small" onClick={handleClose} disabled={saving}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving || loadingVehiculo}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={iconPickerOpen} onClose={() => setIconPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 800 }}>
          Elegir icono
          <IconButton size="small" onClick={() => setIconPickerOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {iconosLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          ) : iconos.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No hay iconos disponibles.</Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {iconos.map((url) => (
                <Tooltip key={url} title={url.split("/").pop()}>
                  <Avatar
                    src={url}
                    variant="rounded"
                    onClick={() => seleccionarIcono(url)}
                    sx={{
                      width: 44,
                      height: 44,
                      cursor: "pointer",
                      border: "2px solid",
                      borderColor: form.veh_icono === url ? "primary.main" : "transparent",
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
